import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/shared/context/AuthContext";

/**
 * Role-aware guard for the driver app.
 *
 * Symmetric enforcement — the same guard handles two mismatches:
 *
 *  1. Drivers on rider surfaces. When `user.is_driver === true` and the
 *     current path is anything other than the allow-listed driver paths,
 *     we redirect to `/driver` (replace=true so Back doesn't bounce).
 *     Without this, tapping the brand logo or a deep link puts a driver
 *     onto the rider booking UI, looking for all the world like their
 *     driver session was lost — the #1 driver-partner complaint.
 *
 *  2. Riders on driver surfaces (Feb 2026). When a logged-in rider hits
 *     `/driver` or `/driver/assigned`, `ProtectedRoute` used to let them
 *     through and the marketplace page would then 403 on its data
 *     fetches — a confusing "Driver access required" toast on a fully
 *     rendered marketplace shell. We now short-circuit at the router
 *     level and send them home to `/` before anything renders.
 *
 * Loading + logged-out states are no-ops (ProtectedRoute handles the
 * logged-out case; loading resolves on next tick).
 */
const ALLOWED_DRIVER_PATHS = new Set([
  "/driver",
  "/driver/assigned",
  "/login",
  "/forgot-password",
  "/reset-password",
]);

export default function DriverRouteGuard() {
  const { user, loading } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    const path = loc.pathname || "/";
    if (user.is_driver) {
      // Driver on a rider surface → bounce to /driver.
      if (!ALLOWED_DRIVER_PATHS.has(path)) {
        nav("/driver", { replace: true });
      }
      return;
    }
    // Non-driver landed on any /driver surface → send home before the
    // marketplace fires a 403-triggering fetch.
    if (path === "/driver" || path.startsWith("/driver/")) {
      nav("/", { replace: true });
    }
  }, [user, loading, loc.pathname, nav]);

  return null;
}
